import { useCallback, useMemo, useState } from 'react';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { CompartmentView } from '@ekkogmbh/apisdk';
import { Stack, Typography } from '@mui/material';
import { StyledCheckbox } from 'src/Common/Components/Forms/StyledCheckbox';
import ObjectField from 'src/Common/Components/DataTable/ObjectField';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import usePagination from 'src/Common/Providers/PaginationProvider';
import useDataTable from 'src/Common/Providers/DataTableProvider';

import useTaskCollections from 'src/Common/Providers/TaskCollectionProvider';

interface Props {
  variant: 'delete' | 'sync';
  onDismiss: () => void;
}

export const SelectedCompartmentsDialog = ({ variant, onDismiss }: Props): React.JSX.Element => {
  const { subscribe } = useTaskCollections();
  const { clearSelection, selectedData, isFilterSelect, filtering, commitFilter } = useDataTable();
  const { api } = useEkko();
  const { refresh } = usePagination();

  const [isProcessing, startProcessing] = useRequest();
  const [isLinksOnly, setLinksOnly] = useState<boolean>(false);

  const coordinates = useMemo(() => selectedData.map((c) => (c as CompartmentView).coordinate), [selectedData]);

  const title = useMemo(() => {
    const entityType = isLinksOnly ? 'Links' : 'Compartments';

    switch (variant) {
      case 'sync':
        return 'Synchronize linked devices with ' + entityType;
      case 'delete':
      default:
        return 'Delete selected ' + entityType;
    }
  }, [variant, isLinksOnly]);

  /** Change to the filter based variant when set */
  const actualVariant = useMemo(() => {
    switch (variant) {
      case 'delete':
        return isFilterSelect ? 'filterDelete' : variant;
      default:
      case 'sync':
        return isFilterSelect ? 'filterSync' : variant;
    }
  }, [variant, isFilterSelect]);

  const content = useMemo(() => {
    if (isFilterSelect) {
      const { filter } = filtering;

      // TODO: add template, technology pickers. But template picker needs some work to allow picking without a coordinate.
      const deleteLinksOptions =
        variant === 'delete' ? (
          <>
            <StyledCheckbox
              value={isLinksOnly}
              label={'Delete Links Only'}
              onChange={() => setLinksOnly(!isLinksOnly)}
            />
          </>
        ) : (
          <></>
        );

      if (filter === undefined) {
        return (
          <Stack>
            {deleteLinksOptions}
            <Typography fontWeight={500} textAlign={'center'} color={'red'}>
              {'all accessible compartments selected'}
            </Typography>
          </Stack>
        );
      }

      // Extract the applied filter text
      const filterParams = (
        <ObjectField
          fields={Object.keys(filter as Record<string, string>).map((key) => ({ key, value: filter[key] }))}
        />
      );

      return (
        <Stack direction={'column'}>
          {deleteLinksOptions}
          <Typography textAlign={'left'}>{'Compartments matching search input'}</Typography>
          {filterParams}
        </Stack>
      );
    }

    const identMax = 10;
    const identCount = coordinates.length;
    const single = coordinates.length === 1;

    let identifiersString = coordinates.slice(0, identMax).join(', ');
    if (identCount > identMax) {
      identifiersString += '... (' + (identCount - identMax) + ' more)';
    }

    let betterVariant: string;
    switch (variant) {
      case 'sync':
        betterVariant = 'Synchronize';
        break;
      default:
      case 'delete':
        betterVariant = 'Delete';
        break;
    }

    if (single) {
      return <Typography>{`${betterVariant} compartment with Coordinate ${identifiersString}?`}</Typography>;
    }

    return (
      <div>
        <div>
          {betterVariant} <span style={{ fontWeight: 700 }}>{identCount}</span> Compartments?
        </div>
        <div
          style={{
            fontSize: 12,
            marginTop: 18,
          }}
        >
          <span style={{ fontWeight: 700 }}>Coordinates:</span> {identifiersString}
        </div>
      </div>
    );
  }, [isFilterSelect, isLinksOnly, filtering.filter, coordinates, variant]);

  const apply = useCallback(() => {
    const { filter } = filtering;

    startProcessing(
      async () => {
        switch (actualVariant) {
          case 'filterDelete': {
            if (isLinksOnly) {
              await api.compartmentFilterDeleteLinks(filter, {}, subscribe);
            } else {
              await api.compartmentFilterDelete(filter, subscribe);
            }
            break;
          }
          case 'filterSync':
            await api.compartmentFilterSync(filter, subscribe);
            break;
          case 'sync': {
            await api.syncCompartmentLinks(coordinates, [], subscribe);
            break;
          }
          case 'delete':
          default: {
            await api.bulkDeleteCompartments(coordinates, subscribe);
          }
        }
      },
      () => {
        clearSelection();
        if (actualVariant === 'filterDelete') {
          commitFilter({});
        }
        if (variant === 'delete') {
          refresh();
        }
        onDismiss();
      },
    );
  }, [actualVariant, variant, filtering.filter, isLinksOnly, coordinates]);

  return (
    <GenericDialog
      type={'confirmation'}
      open={true}
      title={title}
      text={content}
      onClose={onDismiss}
      onConfirm={apply}
      checkmarkSpinner={isProcessing}
      timedOkButton={true}
      minHeight={180}
      minWidth={564}
    />
  );
};
